import {
  Collapse,
  createStyles,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  TablePagination,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Info, KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { useGenericStyles } from 'common/genericStyles';
import { ContaDepositoResponse, InstrumentosFinanceirosFilter } from 'common/genericTypes';
import { SmSpace } from 'm2m.components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addCpfCnpjMask } from 'utils/mask';
import { formatNumber } from 'utils/number';
import { TextField as MUITextField } from '@material-ui/core';

const useStyles = makeStyles(() =>
  createStyles({
    telefone: {
      width: '100px',
    },
    contaDeposito: {
      width: '120px',
    },
    cpfCnpj: {
      width: '145px',
    },
  })
);

const normalizeContasDepositoResult = (raw: unknown): Array<ContaDepositoResponse> => {
  if (!Array.isArray(raw)) return [];
  if (raw.length === 0) return [];

  const first: any = raw[0];

  if (first && Array.isArray(first.posicoes) && typeof first.codigoInstrumentoFinanceiro === 'string') {
    return raw as Array<ContaDepositoResponse>;
  }

  if (first && Array.isArray(first.investidores) && (first.codigo || first.codigoInstrumentoFinanceiro)) {
    return (raw as Array<any>).map((item) => {
      const codigo = String(item?.codigo ?? item?.codigoInstrumentoFinanceiro ?? '');
      const quantidadeTotal = Number(item?.quantidadeTotal ?? item?.quantidade ?? 0);
      const investidores = Array.isArray(item?.investidores) ? item.investidores : [];

      const posicoes = investidores.map((inv: any, index: number) => {
        const quantidade = Number(inv?.quantidade ?? 0);
        const volumeFinanceiro = inv?.volumeFinanceiro != null ? Number(inv.volumeFinanceiro) : undefined;

        return {
          saldoTotal: Number.isFinite(quantidade) ? quantidade : 0,
          saldoDisponivel: Number.isFinite(quantidade) ? quantidade : 0,
          custodianteRazaoSocial: String(inv?.razaoSocialCustodiante ?? ''),
          volumeFinanceiro: volumeFinanceiro != null && Number.isFinite(volumeFinanceiro) ? volumeFinanceiro : undefined,
          investidor: {
            id: String(inv?.cpfCnpj ?? inv?.razaoSocialInvestidor ?? `${codigo}-${index}`),
            contaDeposito: String(inv?.contaDeposito ?? ''),
            razaoSocial: String(inv?.razaoSocialInvestidor ?? ''),
            cpfCnpj: String(inv?.cpfCnpj ?? ''),
          },
        };
      });

      return {
        codigoInstrumentoFinanceiro: codigo,
        quantidadeInstrumentoFinanceiro: Number.isFinite(quantidadeTotal) ? quantidadeTotal : 0,
        posicoes,
      } as ContaDepositoResponse;
    });
  }

  const grouped = new Map<string, any>();

  for (const item of raw as Array<any>) {
    const codigo = String(item?.codigo ?? item?.codigoInstrumentoFinanceiro ?? '');
    if (!codigo) continue;

    const existing = grouped.get(codigo);

    const quantidade = Number(item?.quantidade ?? 0);
    const posicao = {
      saldoTotal: Number.isFinite(quantidade) ? quantidade : 0,
      saldoDisponivel: Number.isFinite(quantidade) ? quantidade : 0,
      custodianteRazaoSocial: String(item?.razaoSocialCustodiante ?? ''),
      volumeFinanceiro:
        item?.volumeFinanceiro != null && Number.isFinite(Number(item.volumeFinanceiro))
          ? Number(item.volumeFinanceiro)
          : undefined,
      investidor: {
        id: String(item?.cpfCnpj ?? item?.razaoSocialInvestidor ?? codigo),
        contaDeposito: String(item?.contaDeposito ?? ''),
        razaoSocial: String(item?.razaoSocialInvestidor ?? ''),
        cpfCnpj: String(item?.cpfCnpj ?? ''),
      },
    };

    if (!existing) {
      grouped.set(codigo, {
        codigoInstrumentoFinanceiro: codigo,
        quantidadeInstrumentoFinanceiro: Number.isFinite(quantidade) ? quantidade : 0,
        posicoes: [posicao],
      });
      continue;
    }

    existing.quantidadeInstrumentoFinanceiro =
      Number(existing.quantidadeInstrumentoFinanceiro ?? 0) + (Number.isFinite(quantidade) ? quantidade : 0);
    existing.posicoes = [...(existing.posicoes ?? []), posicao];
  }

  return Array.from(grouped.values()) as Array<ContaDepositoResponse>;
};

const ListaContasDeposito: React.FC = () => {
  const postContasDepositoPaginatedApi = useSelector(
    (state: RootState) => state.instrumentoApi.postContasDepositoPaginated
  );

  const dispatch = useDispatch();
  const classes = useStyles();
  const genericClasses = useGenericStyles();

  const [expandedInstrumento, setExpandedInstrumento] = useState<string>('');
  const [instrumentoContasDeposito, setInstrumentoContasDeposito] = useState<Array<ContaDepositoResponse>>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [filterCodigoInstrumento, setFilterCodigoInstrumento] = useState<string>('');
  const [selectedCodigoInstrumentoFinanceiro, setSelectedCodigoInstrumentoFinanceiro] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>(false);
  const lastRequestId = useRef(0);

  const getContasDeposito = useCallback(async () => {
    const requestId = ++lastRequestId.current;
    try {
      const result = await postContasDepositoPaginatedApi(
        dispatch,
        {
          page: page + 1,
          pageSize: rowsPerPage,
          instrumentosFinanceirosFilter: selectedCodigoInstrumentoFinanceiro
            ? ([
                {
                  columnType: 'CODIGO',
                  operationFilterType: 'CONTAINS',
                  value: selectedCodigoInstrumentoFinanceiro,
                } as InstrumentosFinanceirosFilter,
              ] as Array<InstrumentosFinanceirosFilter>)
            : undefined,
        },
        true
      );
      if (requestId !== lastRequestId.current) return;
      setInstrumentoContasDeposito(normalizeContasDepositoResult(result?.result));
      setTotalCount(result?.totalCount ?? 0);
      setHasError(false);
    } catch (error) {
      if (requestId !== lastRequestId.current) return;
      console.error('Erro ao buscar contas de depósito:', error);
      setHasError(true);
    }
  }, [dispatch, page, postContasDepositoPaginatedApi, rowsPerPage, selectedCodigoInstrumentoFinanceiro]);

  const handleToggleMoreDetails = (codigo: string) => {
    setExpandedInstrumento(expandedInstrumento === codigo ? '' : codigo);
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      const sanitized = filterCodigoInstrumento.trim().toUpperCase().replace(/[^A-Z0-9\-.]/g, '');
      setExpandedInstrumento('');
      setPage(0);
      setSelectedCodigoInstrumentoFinanceiro(sanitized);
    }, 400);

    return () => clearTimeout(handle);
  }, [filterCodigoInstrumento]);

  useEffect(() => {
    getContasDeposito();
  }, [getContasDeposito]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setExpandedInstrumento('');
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExpandedInstrumento('');
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedInstrumentos = instrumentoContasDeposito;
  const tableCount = totalCount;

  return (
    <>
      <div style={{ maxWidth: 520 }}>
        <MUITextField
          label="Instrumento Financeiro"
          variant="outlined"
          placeholder={'Digite o código do instrumento'}
          value={filterCodigoInstrumento}
          onChange={(e) => setFilterCodigoInstrumento(e.target.value)}
          fullWidth
        />
      </div>
      <SmSpace />

      {hasError ? (
        <Typography variant="body2" color="error" style={{ marginTop: '21px' }}>
          Erro ao carregar dados. Tente novamente.
        </Typography>
      ) : pagedInstrumentos.length === 0 ? (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: '21px' }}>
          Nenhum resultado encontrado
        </Typography>
      ) : (
        (pagedInstrumentos ?? []).map((instrumento) => (
          <div
            key={instrumento.codigoInstrumentoFinanceiro}
            style={{ border: '1px solid #E5E5E5', borderRadius: 8, marginTop: '21px' }}
          >
            <Grid style={{ padding: '10px 20px' }} container alignItems="center" spacing={2}>
              <Grid item md={2}>
                <Typography variant="body2">{instrumento.codigoInstrumentoFinanceiro}</Typography>
              </Grid>
              <Grid item md>
                <Typography variant="body2">
                  {formatNumber(instrumento.quantidadeInstrumentoFinanceiro, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 8,
                  })}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => handleToggleMoreDetails(instrumento.codigoInstrumentoFinanceiro)}
                >
                  {expandedInstrumento === instrumento.codigoInstrumentoFinanceiro ? (
                    <KeyboardArrowUp />
                  ) : (
                    <KeyboardArrowDown />
                  )}
                </IconButton>
              </Grid>
            </Grid>
            <Collapse
              in={expandedInstrumento === instrumento.codigoInstrumentoFinanceiro}
              timeout="auto"
              unmountOnExit
              style={{ backgroundColor: '#F6F6F9', padding: 20 }}
            >
              <Grid container spacing={2}>
                <Grid item md={2}>
                  <Typography className={classes.contaDeposito} variant="body2" color="textSecondary">
                    {'Conta Depósito'}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant="body2" color="textSecondary">
                    {'Nome/Razão Social'}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography className={classes.cpfCnpj} variant="body2" color="textSecondary">
                    {'CPF/CNPJ'}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant="body2" color="textSecondary">
                    {'Quantidade'}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant="body2" color="textSecondary" className={classes.telefone}>
                    {'Custodiante'}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant="body2" color="textSecondary">
                    {'Volume Financeiro'}
                  </Typography>
                </Grid>
              </Grid>
              <SmSpace />
              {(instrumento.posicoes ?? []).map((posicao) => (
                <React.Fragment key={posicao.investidor.id}>
                  <Divider variant="fullWidth" />
                  <SmSpace />
                  <Grid container spacing={2} alignItems="center">
                    <Grid item md={2}>
                      <Typography className={classes.contaDeposito} variant="body2">
                        {posicao.investidor.contaDeposito}
                      </Typography>
                    </Grid>
                    <Grid item md={2}>
                      <Tooltip title={posicao.investidor.razaoSocial} aria-label={posicao.investidor.razaoSocial}>
                        <Typography className={genericClasses.wrapTxt} variant="body2">
                          {posicao.investidor.razaoSocial}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid item md={2}>
                      <Typography className={classes.cpfCnpj} variant="body2">
                        {addCpfCnpjMask(posicao.investidor.cpfCnpj)}
                      </Typography>
                    </Grid>
                    <Grid item md={2} style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" style={{ marginRight: '5px' }}>
                        {formatNumber(posicao.saldoTotal, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}
                      </Typography>
                      {posicao.saldoTotal > posicao.saldoDisponivel && (
                        <Tooltip
                          title={`Quantidade bloqueada: ${formatNumber(posicao.saldoTotal - posicao.saldoDisponivel, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 8,
                          })}`}
                          aria-label={`Quantidade bloqueada: ${formatNumber(
                            posicao.saldoTotal - posicao.saldoDisponivel,
                            { minimumFractionDigits: 0, maximumFractionDigits: 8 }
                          )}`}
                        >
                          <Info fontSize="small" />
                        </Tooltip>
                      )}
                    </Grid>
                    <Grid item md={2} style={{ overflow: 'hidden' }}>
                      <Tooltip title={posicao.custodianteRazaoSocial} aria-label={posicao.custodianteRazaoSocial}>
                        <Typography className={genericClasses.wrapTxt} variant="body2">
                          {posicao.custodianteRazaoSocial}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid item md={2}>
                      <Typography variant="body2">
                        {posicao.volumeFinanceiro != null
                          ? formatNumber(posicao.volumeFinanceiro, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                              style: 'currency',
                              currency: 'BRL',
                            })
                          : '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                  <SmSpace />
                </React.Fragment>
              ))}
            </Collapse>
          </div>
        ))
      )}

      <TablePagination
        component="div"
        count={tableCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 20, 40, 60, 100]}
        labelRowsPerPage="Linhas por página"
        labelDisplayedRows={({ from, to, count }) => {
          const safeCount = count === -1 ? tableCount : count;
          return `${from} - ${to} de ${safeCount}`;
        }}
      />
    </>
  );
};

export default ListaContasDeposito;
